import { useCallback, useMemo, useRef, useState } from 'react';
import { ClassNames } from '@emotion/react';
import PropTypes from 'prop-types';
import DiagramFrame, { DiagramGradients, stageWidthCap } from './vehicleDiagram/DiagramFrame';
import TyreGlyph, { TyreGradients } from './vehicleDiagram/TyreGlyph';
import { buildVehicleGeometry } from './vehicleDiagram/layoutGeometry';
import { STATE_ORDER, statusOf, TYRE_STATES } from './vehicleDiagram/diagramTheme';
import { describePosition } from './vehicleDiagram/positionDescription';

let instanceCount = 0;

const useInstanceId = () => {
  const ref = useRef(null);
  if (ref.current === null) {
    instanceCount += 1;
    ref.current = `tmv${instanceCount}`;
  }
  return ref.current;
};

const ARROWS = {
  ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
};

const describe = (position) => {
  const fitment = position.current_fitment;
  return fitment
    ? `${describePosition(position)} · tyre ${fitment.tyre_id}`
    : describePosition(position);
};

const specLine = (geometry) => {
  const parts = [`${geometry.positionCount} wheels`, `${geometry.axleCount} ${geometry.axleCount === 1 ? 'axle' : 'axles'}`];
  if (geometry.axles.some((axle) => axle.dual)) parts.push('dual fitment');
  return parts.join(' · ');
};

const classLabelOf = (configuration) => (
  configuration?.vehicle_class?.name
  || configuration?.layout_template?.vehicle_class?.name
  || configuration?.layout_template?.name
  || 'Vehicle layout'
);

const coverageHint = (renderCoverage, renderFallbackReason) => {
  if (renderFallbackReason === 'webgl_unavailable') return '3D unavailable in this browser · Accurate 2D';
  if (renderCoverage?.state === 'configured_2d') return '3D render not yet available · Accurate 2D';
  if (renderCoverage?.state === 'unsupported') return 'Render identity pending confirmation · Accurate 2D';
  return 'Top view';
};

const VehicleLayoutSvg = ({
  vehicle,
  configuration,
  selectedPositionId,
  onSelectPosition,
  renderCoverage,
  renderFallbackReason,
}) => {
  const uid = useInstanceId();
  const geometry = useMemo(() => buildVehicleGeometry(configuration, vehicle), [configuration, vehicle]);
  const [focusedId, setFocusedId] = useState(null);
  const tyreNodes = useRef({});

  const counts = useMemo(() => {
    const tally = Object.fromEntries(STATE_ORDER.map((key) => [key, 0]));
    (configuration?.positions || []).forEach((position) => {
      const key = statusOf(position);
      tally[key] = (tally[key] || 0) + 1;
    });
    return tally;
  }, [configuration]);

  const onArrow = useCallback((event) => {
    const step = ARROWS[event.key];
    if (!step || !geometry) return;
    const [dx, dy] = step;
    const current = geometry.tyres.find((tyre) => tyre.position.id === focusedId) || geometry.tyres[0];
    const sameRow = (tyre) => Math.abs(tyre.y - current.y) < 1;

    const next = geometry.tyres
      .filter((tyre) => (dx
        ? sameRow(tyre) && Math.sign(tyre.x - current.x) === dx
        : Math.sign(tyre.y - current.y) === dy))
      .sort((a, b) => (dx
        ? Math.abs(a.x - current.x) - Math.abs(b.x - current.x)
        : (Math.abs(a.y - current.y) - Math.abs(b.y - current.y)) || (Math.abs(a.x - current.x) - Math.abs(b.x - current.x))))[0];

    if (!next) return;
    event.preventDefault();
    tyreNodes.current[next.position.id]?.focus();
  }, [focusedId, geometry]);

  if (!geometry) {
    return (
      <div className="tm-plate tm-plate--empty">
        <p className="tm-plate__fallback">No tyre positions are configured.</p>
      </div>
    );
  }

  const { tyres, width, height } = geometry;
  const classLabel = classLabelOf(configuration);
  const tabbableId = tyres.some((tyre) => tyre.position.id === (focusedId || selectedPositionId))
    ? (focusedId || selectedPositionId)
    : tyres[0].position.id;

  return (
    <ClassNames>
      {({ css }) => (
        <figure
      className="tm-plate"
      data-render-source={renderCoverage?.identity?.source || 'unmapped'}
      data-render-coverage={renderFallbackReason === 'webgl_unavailable'
        ? 'webgl_unavailable'
        : (renderCoverage?.state || 'unsupported')}
      data-render-model-key={renderCoverage?.identity?.renderModelKey}
      data-render-layout-key={renderCoverage?.identity?.renderLayoutKey}
    >
      <figcaption className="tm-plate__head">
        <span className="tm-plate__spec">{specLine(geometry)}</span>
        <span className="tm-plate__hint">{classLabel} · {coverageHint(renderCoverage, renderFallbackReason)}</span>
      </figcaption>

      <div className="tm-plate__stage">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className={`tm-plate__svg ${css({ maxWidth: stageWidthCap(geometry, 500) })}`}
          role="group"
          aria-label={`${vehicle?.registration_num || configuration?.vehicle_number || 'Vehicle'} ${classLabel} tyre position map. Use the arrow keys to move between wheels.`}
          onKeyDown={onArrow}
        >
          <defs>
            <TyreGradients id={`${uid}-rubber`} />
            <DiagramGradients uid={uid} />
          </defs>

          <DiagramFrame geometry={geometry} uid={uid} />

          {tyres.map((tyre) => (
            <TyreGlyph
              key={tyre.position.id}
              tyre={tyre}
              status={statusOf(tyre.position)}
              code={tyre.position.code}
              label={describe(tyre.position)}
              selected={String(tyre.position.id) === String(selectedPositionId)}
              tabbable={String(tyre.position.id) === String(tabbableId)}
              gradientId={`${uid}-rubber`}
              nodeRef={(node) => { tyreNodes.current[tyre.position.id] = node; }}
              onFocus={() => setFocusedId(tyre.position.id)}
              onSelect={onSelectPosition}
            />
          ))}
        </svg>
      </div>

        <ul className="tm-plate__legend">
          {STATE_ORDER.map((key) => (
            <li key={key} className="tm-plate__legend-item" data-zero={counts[key] ? undefined : true}>
              <span className={`tm-plate__legend-dot ${css({ backgroundColor: TYRE_STATES[key].color })}`} />
              <span className="tm-plate__legend-label">{TYRE_STATES[key].label}</span>
              <span className="tm-plate__legend-count">{counts[key]}</span>
            </li>
          ))}
        </ul>
      </figure>
      )}
    </ClassNames>
  );
};

VehicleLayoutSvg.propTypes = {
  vehicle: PropTypes.object,
  configuration: PropTypes.object,
  selectedPositionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectPosition: PropTypes.func.isRequired,
  renderCoverage: PropTypes.object,
  renderFallbackReason: PropTypes.string,
};

export default VehicleLayoutSvg;
